import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/store/i18n';
import type { QuestionStatus } from '@/types';

type Tone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'brand';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  dot?: boolean;
}

const tones: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  brand: 'bg-brand-50 text-brand-700 border-brand-200',
};

export function Badge({
  className,
  tone = 'neutral',
  dot = false,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium border rounded-full whitespace-nowrap',
        tones[tone],
        className
      )}
      {...rest}
    >
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

const STATUS_TONE: Record<QuestionStatus, Tone> = {
  approved: 'success',
  pending: 'warning',
  draft: 'neutral',
  rejected: 'danger',
};

export function StatusBadge({ status }: { status: QuestionStatus }) {
  const t = useT();
  const STATUS_LABEL: Record<QuestionStatus, string> = {
    approved: t('badge.approved'),
    pending: t('badge.pending'),
    draft: t('badge.draft'),
    rejected: t('badge.rejected'),
  };
  return (
    <Badge tone={STATUS_TONE[status]} dot>
      {STATUS_LABEL[status]}
    </Badge>
  );
}
